import { CareRow, CareRows, TerminationRecord } from './types';
import { CARE_CODE_LABELS } from './constants';
import { filterRows, RowFilter } from './presentation';

export const patientLabel = (row: Pick<CareRow, 'patient_name' | 'hero_user_id'>): string =>
    row.patient_name?.trim() || `Hero user ${row.hero_user_id}`;

// Names first, then unnamed records by ID. Sort display copies without changing API data.
export const comparePatients = (a: CareRow, b: CareRow): number => {
    const aName = patientLabel(a);
    const bName = patientLabel(b);
    const aUnnamed = !a.patient_name?.trim();
    const bUnnamed = !b.patient_name?.trim();
    return (
        Number(aUnnamed) - Number(bUnnamed) ||
        aName.localeCompare(bName, undefined, { sensitivity: 'base', numeric: true }) ||
        a.hero_user_id - b.hero_user_id
    );
};

const codeOrder = Array.from(CARE_CODE_LABELS.keys());
export const compareCodes = (a: CareRow, b: CareRow): number => {
    const aIndex = codeOrder.indexOf(a.code);
    const bIndex = codeOrder.indexOf(b.code);
    return (
        (aIndex < 0 ? codeOrder.length : aIndex) - (bIndex < 0 ? codeOrder.length : bIndex) ||
        a.code.localeCompare(b.code, undefined, { numeric: true }) ||
        a.charge_id - b.charge_id
    );
};

const changeTime = (row: CareRow): number => {
    const timestamp = Date.parse(row.changes.changed_at ?? '');
    return Number.isFinite(timestamp) ? timestamp : Number.NEGATIVE_INFINITY;
};

// Rendering and downloading use the same patient groups and code order.
export const patientTableRows = (rows: CareRows | undefined, filter: RowFilter) => {
    const visible = rows ? filterRows(rows.rows, filter) : undefined;
    const patients = new Map<number, CareRow[]>();
    const allPatientCodes = new Map<number, CareRow[]>();
    const changedPatients = new Map<number, CareRow>();
    const rowsById = new Map<number, CareRow>();
    rows?.rows.forEach((row) => {
        rowsById.set(row.charge_id, row);
        const codes = allPatientCodes.get(row.hero_user_id);
        if (codes) codes.push(row);
        else allPatientCodes.set(row.hero_user_id, [row]);
        if (row.changes.changed) {
            const previous = changedPatients.get(row.hero_user_id);
            const timestamp = changeTime(row);
            const previousTimestamp = previous ? changeTime(previous) : Number.NEGATIVE_INFINITY;
            if (!previous || timestamp > previousTimestamp ||
                (timestamp === previousTimestamp &&
                    (row.changes.latest_event_id ?? 0) > (previous.changes.latest_event_id ?? 0)))
                changedPatients.set(row.hero_user_id, row);
        }
    });
    visible?.forEach((row) => {
        const codes = patients.get(row.hero_user_id);
        if (codes) codes.push(row);
        else patients.set(row.hero_user_id, [row]);
    });
    const sortedPatients = Array.from(patients.entries()).sort(([, a], [, b]) =>
        comparePatients(a[0], b[0])
    );
    sortedPatients.forEach(([, codes]) => codes.sort(compareCodes));
    const endedPatients = new Map<number, TerminationRecord[]>();
    if (!filter.currentStatus && !filter.code && !filter.billableOnly &&
        !filter.changedOnly && !filter.missingOnly) {
        rows?.termination_records.forEach((record) => {
            if (allPatientCodes.has(record.hero_user_id)) return;
            const records = endedPatients.get(record.hero_user_id);
            if (records) records.push(record);
            else endedPatients.set(record.hero_user_id, [record]);
        });
    }
    const sortedEndedPatients = Array.from(endedPatients.entries()).sort(([a], [b]) => a - b);
    const endedEnrollmentCount = sortedEndedPatients.reduce((total, [, records]) => total + records.length, 0);
    return {
        visible, patients, allPatientCodes, changedPatients, rowsById,
        sortedPatients, endedPatients, sortedEndedPatients, endedEnrollmentCount
    };
};
